import React from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ProjectBasedLearningCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const SkillsToDevelopStep: React.FC<
  StepComponentProps<ProjectBasedLearningCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();

  const handleSkillsToDevelopChange = (value: string) => {
    onDataChange({ skillsToDevelop: value });
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("projectBasedLearning.steps.skills.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("projectBasedLearning.steps.skills.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("projectBasedLearning.form.skillsToDevelop")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("projectBasedLearning.form.skillsToDevelopDesc")}
        </p>

        <div className="space-y-4">
          <textarea
            value={data.skillsToDevelop || ""}
            onChange={(e) => handleSkillsToDevelopChange(e.target.value)}
            className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 min-h-[150px]`}
            placeholder={t(
              "projectBasedLearning.form.skillsToDevelopPlaceholder",
            )}
          />
        </div>

        {/* Skills Categories */}
        <div className="mt-6 space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100">
            {t("projectBasedLearning.form.skillsCategories")}:
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 21st Century Skills */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <h5 className="font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
                <span>🧠</span>
                {t("projectBasedLearning.skillsCategory.cognitive")}
              </h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• {t("projectBasedLearning.skills.criticalThinking")}</li>
                <li>• {t("projectBasedLearning.skills.problemSolving")}</li>
                <li>• {t("projectBasedLearning.skills.creativity")}</li>
                <li>• {t("projectBasedLearning.skills.analysis")}</li>
              </ul>
            </div>

            {/* Communication Skills */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <h5 className="font-medium text-green-700 dark:text-green-300 mb-2 flex items-center gap-2">
                <span>💬</span>
                {t("projectBasedLearning.skillsCategory.communication")}
              </h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• {t("projectBasedLearning.skills.presentation")}</li>
                <li>• {t("projectBasedLearning.skills.writing")}</li>
                <li>• {t("projectBasedLearning.skills.collaboration")}</li>
                <li>• {t("projectBasedLearning.skills.teamwork")}</li>
              </ul>
            </div>

            {/* Technical Skills */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
              <h5 className="font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-2">
                <span>⚙️</span>
                {t("projectBasedLearning.skillsCategory.technical")}
              </h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• {t("projectBasedLearning.skills.research")}</li>
                <li>• {t("projectBasedLearning.skills.technology")}</li>
                <li>• {t("projectBasedLearning.skills.dataAnalysis")}</li>
                <li>• {t("projectBasedLearning.skills.design")}</li>
              </ul>
            </div>

            {/* Personal Skills */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
              <h5 className="font-medium text-orange-700 dark:text-orange-300 mb-2 flex items-center gap-2">
                <span>🌟</span>
                {t("projectBasedLearning.skillsCategory.personal")}
              </h5>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• {t("projectBasedLearning.skills.selfManagement")}</li>
                <li>• {t("projectBasedLearning.skills.timeManagement")}</li>
                <li>• {t("projectBasedLearning.skills.leadership")}</li>
                <li>• {t("projectBasedLearning.skills.adaptability")}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
